import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, SlidersHorizontal, Calendar, ChevronRight, Sparkles, TrendingUp, Clock } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeCategory, setActiveCategory] = useState('tutti');

  useEffect(() => {
    // Try to load shop-specific sections (get first shop if no specific one)
    const loadSections = async () => {
      try {
        // First try to get a shop to use
        const shopsRes = await axios.get(`${API}/categories`);
        const { data } = await axios.get(`${API}/home/sections`);
        setSections(data);
      } catch(e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadSections();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const params = { search, limit: 20 };
      if (activeCategory !== 'tutti') params.category = activeCategory;
      const { data } = await axios.get(`${API}/products`, { params });
      setSearchResults(data.products || []);
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  };

  const fetchByCategory = async (cat) => {
    setActiveCategory(cat);
    if (cat === 'tutti') { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/products`, { params: { category: cat, limit: 20 } });
      setSearchResults(data.products || []);
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  };

  const shop = sections?.shop;
  const categories = sections?.categories || ['Novità', 'In Offerta', 'Più Venduti', 'Altro'];
  const [coverSrc, setCoverSrc] = useState(null);

  useEffect(() => {
    if (!shop?.cover_path) return;
    axios.get(`${API}/files/${shop.cover_path}`, { responseType: 'blob' })
      .then(res => setCoverSrc(URL.createObjectURL(res.data)))
      .catch(() => {});
  }, [shop?.cover_path]);

  return (
    <div data-testid="home-page" className="pb-safe">
      {/* Hero */}
      <div className="relative h-48 sm:h-64 overflow-hidden">
        <img
          src={coverSrc || "https://images.unsplash.com/photo-1721510275545-5e5163f17bf0?w=800&q=80"}
          alt="Shop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A2624]/80 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>
            {shop?.name || 'PhotoVendo'}
          </h1>
          <p className="text-white/80 text-sm mt-1" style={{ fontFamily: 'Manrope' }}>
            {shop?.description || 'Le migliori offerte del giorno'}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 -mt-5 relative z-10">
        <form onSubmit={handleSearch} className="flex items-center bg-white rounded-2xl shadow-md border border-[#E8E3D9] overflow-hidden">
          <Search size={18} className="ml-4 text-[#877E78]" />
          <input
            data-testid="search-input"
            type="text"
            placeholder="Cerca prodotti..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-3 py-3.5 text-sm bg-transparent outline-none text-[#2A2624] placeholder:text-[#877E78]"
          />
          <button data-testid="search-btn" type="submit" className="px-4 py-3.5 bg-[#C25934] text-white hover:bg-[#A64B2B] transition-colors">
            <SlidersHorizontal size={18} />
          </button>
        </form>
      </div>

      {/* Categories */}
      {/* Dynamic Tag Sections — auto-generated from product tags */}
      {sections?.tag_sections?.length > 0 && (
        <div className="px-4 mt-5">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
            <button
              data-testid="category-tutti"
              onClick={() => fetchByCategory('tutti')}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                activeCategory === 'tutti' ? 'bg-[#C25934] text-white shadow-md' : 'bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6]'
              }`}
            >
              Tutti
            </button>
            {sections.tag_sections.map(s => (
              <button
                key={s.tag}
                data-testid={`tag-filter-${s.tag}`}
                onClick={() => fetchByCategory(s.tag)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                  activeCategory === s.tag ? 'bg-[#C25934] text-white shadow-md' : 'bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6]'
                }`}
              >
                {s.tag} <span className="ml-1 opacity-70">({s.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults !== null ? (
        <div className="px-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              {searching ? 'Ricerca...' : `${searchResults.length} risultati`}
            </h2>
            <button onClick={() => { setSearchResults(null); setSearch(''); setActiveCategory('tutti'); }} className="text-xs text-[#C25934] font-medium">
              Cancella
            </button>
          </div>
          {searchResults.length === 0 ? (
            <div className="text-center py-12">
              <Search size={32} className="mx-auto text-[#877E78] mb-3" />
              <p className="text-[#877E78]">Nessun prodotto trovato</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {searchResults.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="px-4 mt-4 space-y-6">
          <div className="h-6 skeleton-shimmer rounded w-1/3" />
          <div className="grid grid-cols-2 gap-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-[#E8E3D9] overflow-hidden">
                <div className="aspect-[4/5] skeleton-shimmer" />
                <div className="p-3 space-y-2"><div className="h-4 skeleton-shimmer rounded" /><div className="h-5 skeleton-shimmer rounded w-1/2" /></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4">
          {/* Today's Offers */}
          {sections?.today_products?.length > 0 && (
            <HomeSection
              title="Offerte di Oggi"
              icon={<Sparkles size={18} className="text-[#C25934]" />}
              products={sections.today_products}
              onSeeAll={sections.today_flyer ? () => navigate(`/volantino/${sections.today_flyer.id}`) : undefined}
            />
          )}

          {/* Dynamic tag-based sections */}
          {sections?.tag_sections?.map(s => (
            <HomeSection
              key={s.tag}
              title={s.tag.charAt(0).toUpperCase() + s.tag.slice(1)}
              icon={<span className="text-base">🏷️</span>}
              products={s.products}
              onSeeAll={() => navigate(`/catalogo?tag=${encodeURIComponent(s.tag)}`)}
            />
          ))}

          {/* Fallback legacy: Most Requested (only if no tag sections) */}
          {(!sections?.tag_sections?.length) && sections?.popular?.length > 0 && (
            <HomeSection
              title="I Più Richiesti"
              icon={<TrendingUp size={18} className="text-[#4A6B53]" />}
              products={sections.popular}
            />
          )}

          {/* Flyer Archive */}
          {sections?.recent_flyers?.length > 0 && (
            <div className="px-4 mt-6 mb-4">
              <h2 className="text-lg font-medium text-[#2A2624] flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit' }}>
                <Calendar size={18} className="text-[#877E78]" /> Volantini Recenti
              </h2>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                {sections.recent_flyers.map(f => (
                  <button
                    key={f.id}
                    data-testid={`flyer-${f.id}`}
                    onClick={() => navigate(`/volantino/${f.id}`)}
                    className="flex-shrink-0 bg-white rounded-xl border border-[#E8E3D9] p-3 min-w-[140px] text-left hover:border-[#C25934]/50 transition-colors"
                  >
                    <p className="text-xs font-bold text-[#C25934]">{f.title || f.date}</p>
                    <p className="text-[10px] text-[#877E78] mt-0.5">{f.product_count} prodotti</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!sections?.today_products?.length && !sections?.latest?.length && (
            <div className="text-center py-16 px-4">
              <div className="w-20 h-20 bg-[#F0EBE1] rounded-full flex items-center justify-center mx-auto mb-4">
                <Search size={32} className="text-[#877E78]" />
              </div>
              <h3 className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                Nessun prodotto ancora
              </h3>
              <p className="text-sm text-[#877E78] mt-1">I prodotti appariranno qui quando verranno pubblicati</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HomeSection({ title, icon, products, onSeeAll }) {
  return (
    <div className="px-4 mt-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-medium text-[#2A2624] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          {icon} {title}
        </h2>
        {onSeeAll && (
          <button onClick={onSeeAll} className="flex items-center gap-0.5 text-xs text-[#C25934] font-medium">
            Vedi tutti <ChevronRight size={14} />
          </button>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
        {products.slice(0, 8).map(p => (
          <div key={p.id} className="flex-shrink-0 w-[160px]">
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </div>
  );
}
